import { Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import { Loading } from "./components/Loading";
import { Protected } from "./components/DashboardShell";
import { lazyWithRefresh } from "./lib/lazy-with-refresh";
import { RouteMetadata } from "./components/RouteMetadata";

const NotFound = lazyWithRefresh(() => import("./pages/NotFound"), "not-found");
const CredentialVerification = lazyWithRefresh(() => import("./pages/CredentialVerification"), "credential-verification");
const Landing = lazyWithRefresh(() => import("./pages/Landing"), "landing");
const Login = lazyWithRefresh(() => import("./pages/Login"), "login");
const StudentDashboard = lazyWithRefresh(
  () => import("./pages/StudentDashboard"),
  "student-dashboard",
);
const ParentDashboard = lazyWithRefresh(
  () => import("./pages/ParentDashboard"),
  "parent-dashboard",
);
const InstructorDashboard = lazyWithRefresh(
  () => import("./pages/InstructorDashboard"),
  "instructor-dashboard",
);
const AdminDashboard = lazyWithRefresh(
  () => import("./pages/AdminDashboard"),
  "admin-dashboard",
);
const CourseDetail = lazyWithRefresh(
  () => import("./pages/CourseDetail"),
  "course-detail",
);
const InstructorDirectory = lazyWithRefresh(
  () => import("./pages/InstructorDirectory"),
  "instructor-directory",
);
const InstructorProfile = lazyWithRefresh(
  () => import("./pages/InstructorProfile"),
  "instructor-profile",
);
const RecordedCourse = lazyWithRefresh(
  () => import("./pages/RecordedCourse"),
  "recorded-course",
);
const Soon = lazyWithRefresh(() => import("./pages/Soon"), "soon");
const Register = lazyWithRefresh(
  () => import("./pages/RegisterFlow"),
  "register",
);
const VerifyEmail = lazyWithRefresh(
  () => import("./pages/VerifyEmail"),
  "verify-email",
);
const ForgotPassword = lazyWithRefresh(
  () => import("./pages/ForgotPassword"),
  "forgot-password",
);
const ResetPassword = lazyWithRefresh(
  () => import("./pages/ResetPasswordFlow"),
  "reset-password",
);
const AcceptInvitation = lazyWithRefresh(
  () => import("./pages/InvitationFlow"),
  "accept-invitation",
);
const PaymentOrder = lazyWithRefresh(
  () => import("./pages/PaymentOrder"),
  "payment-order",
);
const OfferDecision = lazyWithRefresh(
  () => import("./pages/OfferDecision"),
  "offer-decision",
);

function AppHome() {
  const { user } = useAuth();
  return user ? (
    <Navigate to={`/app/${user.role}`} replace />
  ) : (
    <Navigate to="/login" replace />
  );
}

export default function App() {
  return (
    <><RouteMetadata /><Suspense fallback={<Loading />}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/credentials/:credentialCode" element={<CredentialVerification />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/invitations/:token" element={<AcceptInvitation />} />
        <Route path="/offers/:token" element={<OfferDecision />} />
        <Route path="/courses/:slug" element={<CourseDetail />} />
        <Route path="/instructors" element={<InstructorDirectory />} />
        <Route path="/instructors/:id" element={<InstructorProfile />} />
        <Route path="/soon/:feature" element={<Soon />} />
        <Route path="/app" element={<AppHome />} />
        <Route
          path="/app/student"
          element={
            <Protected role="student">
              <StudentDashboard />
            </Protected>
          }
        />
        <Route
          path="/app/student/:section"
          element={
            <Protected role="student">
              <StudentDashboard />
            </Protected>
          }
        />
        <Route
          path="/app/student/recorded/:courseId"
          element={
            <Protected role="student">
              <RecordedCourse />
            </Protected>
          }
        />
        <Route
          path="/app/parent"
          element={
            <Protected role="parent">
              <ParentDashboard />
            </Protected>
          }
        />
        <Route
          path="/app/parent/payments/:orderId"
          element={
            <Protected role="parent">
              <PaymentOrder />
            </Protected>
          }
        />
        <Route
          path="/app/parent/:section"
          element={
            <Protected role="parent">
              <ParentDashboard />
            </Protected>
          }
        />
        <Route
          path="/app/instructor"
          element={
            <Protected role="instructor">
              <InstructorDashboard />
            </Protected>
          }
        />
        <Route
          path="/app/instructor/:section"
          element={
            <Protected role="instructor">
              <InstructorDashboard />
            </Protected>
          }
        />
        <Route
          path="/app/admin"
          element={
            <Protected role="admin">
              <AdminDashboard />
            </Protected>
          }
        />
        <Route
          path="/app/admin/programs/:programId"
          element={
            <Protected role="admin">
              <AdminDashboard />
            </Protected>
          }
        />
        <Route
          path="/app/admin/:section"
          element={
            <Protected role="admin">
              <AdminDashboard />
            </Protected>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense></>
  );
}
